export const TOAST_APPEARANCE_PLATFORM_SETTING_KEY = 'toast_appearance';

export type ToastStyle = 'aurora' | 'rail';

export interface ToastAppearance {
  /** Visual finish for every toast. `aurora` = gradient glass surface, `rail` = light card with a status accent rail. */
  style: ToastStyle;
  /** When true, surfaces use a frosted backdrop blur (auto-disabled on unsupported browsers / prefers-reduced-transparency). */
  translucent: boolean;
}

export const DEFAULT_TOAST_APPEARANCE: ToastAppearance = {
  style: 'aurora',
  translucent: true,
};

const TOAST_STYLES: readonly ToastStyle[] = ['aurora', 'rail'];

const isToastStyle = (value: unknown): value is ToastStyle =>
  typeof value === 'string' && (TOAST_STYLES as readonly string[]).includes(value);

const coerceBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
};

const parseJsonString = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

/**
 * Coerce an arbitrary `platform_settings.value` into a safe {@link ToastAppearance}.
 * Always returns a complete appearance, falling back to {@link DEFAULT_TOAST_APPEARANCE}
 * for missing/invalid fields so the toaster never renders without a finish.
 */
export const resolveToastAppearance = (value: unknown): ToastAppearance => {
  let candidate: unknown = value;

  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    candidate = trimmed.startsWith('{') ? parseJsonString(trimmed) : trimmed;
  }

  if (candidate && typeof candidate === 'object') {
    const record = candidate as Record<string, unknown>;
    return {
      style: isToastStyle(record.style) ? record.style : DEFAULT_TOAST_APPEARANCE.style,
      translucent: coerceBoolean(record.translucent, DEFAULT_TOAST_APPEARANCE.translucent),
    };
  }

  return { ...DEFAULT_TOAST_APPEARANCE };
};
